// ═══════════════════════════════════════════════════════════════════════════════
// TabBuscadorBaterias.jsx — Buscador de batería por vehículo
// Usar: import TabBuscadorBaterias from './TabBuscadorBaterias';
// Colocar en la misma carpeta que GarantiasModule.jsx
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import { YUASA_CATALOGO } from '../data/yuasaCatalogo';

// ─── Constantes de display ────────────────────────────────────────────────────
const CATEGORIAS = [
  { key: 'VEHICULOS',  label: '🚗 Vehículos',   color: '#4cc9f0' },
  { key: 'CAMIONES',   label: '🚛 Camiones',    color: '#ffc107' },
  { key: 'ELECTRICOS', label: '⚡ Eléctricos',  color: '#4caf50' },
  { key: 'MAQUINARIA', label: '🏗️ Maquinaria',  color: '#f72585' },
];

// ─── Badge de referencia de batería ──────────────────────────────────────────
function BatBadge({ label, value, color, bg }) {
  if (!value) return null;
  return (
    <div style={{ background: bg, borderRadius: 8,
      padding: '10px 14px', border: `1px solid ${color}33` }}>
      <div style={{ color: '#555', fontSize: 9, textTransform: 'uppercase',
        letterSpacing: 1, marginBottom: 4 }}>{label}</div>
      <div style={{ color, fontFamily: 'monospace',
        fontWeight: 700, fontSize: 15 }}>{value}</div>
    </div>
  );
}

// ─── Card resultado ───────────────────────────────────────────────────────────
function ResultCard({ r, idx }) {
  const total = useMemo(() => [r].length, [r]);
  return (
    <div style={{
      background: '#1e1e2e', borderRadius: 12,
      border: '1px solid #2a2a3e',
      overflow: 'hidden',
      animation: 'fadeIn .2s ease',
    }}>
      {/* Header del resultado */}
      <div style={{
        background: '#13131f', padding: '12px 16px',
        borderBottom: '1px solid #2a2a3e',
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', flexWrap: 'wrap', gap: 8,
      }}>
        <div>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>
            {r.marca} · {r.modelo}
          </div>
          {r.motor && (
            <div style={{ color: '#aaa', fontSize: 12, marginTop: 2 }}>
              {r.motor}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {r.ano && (
            <span style={{ background: '#2a2a3e', color: '#4cc9f0',
              borderRadius: 5, padding: '3px 10px', fontSize: 11,
              fontFamily: 'monospace' }}>📅 {r.ano}</span>
          )}
          {r.caja && (
            <span style={{ background: '#2a2a3e', color: '#aaa',
              borderRadius: 5, padding: '3px 10px', fontSize: 11 }}>
              Caja: {r.caja}
            </span>
          )}
        </div>
      </div>

      {/* Baterías recomendadas */}
      <div style={{ padding: '14px 16px',
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
        <BatBadge
          label="STD — Estándar"
          value={r.std}
          color="#4cc9f0"
          bg="#001a2a"
        />
        <BatBadge
          label="FULL — Premium"
          value={r.full}
          color="#7209b7"
          bg="#1a0028"
        />
        <BatBadge
          label="Alternativa"
          value={r.alternativa}
          color="#4caf50"
          bg="#0d2218"
        />
      </div>

      {/* Ficha PDF del modelo */}
      <div style={{ padding: '0 16px 12px',
        display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {[r.std, r.full, r.alternativa].filter(Boolean).map((ref, i) => {
          // Extraer modelo limpio para buscar en ficha
          const modelo = ref.replace(/SMF|EFB|MF|\s/gi, '').split('/')[0];
          return (
            <span key={i} style={{ color: '#555', fontSize: 10 }}>
              {i > 0 && '·'} {ref}
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ─── Selector estilizado ──────────────────────────────────────────────────────
function Selector({ label, value, onChange, options, placeholder, disabled }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ color: '#555', fontSize: 10, textTransform: 'uppercase',
        letterSpacing: 0.5 }}>{label}</div>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        style={{
          background: disabled ? '#0a0a14' : '#13131f',
          border: `1px solid ${value ? '#4361ee' : '#2a2a3e'}`,
          borderRadius: 8, color: value ? '#fff' : '#555',
          padding: '10px 12px', fontSize: 13, outline: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          width: '100%',
        }}
      >
        <option value="">{placeholder}</option>
        {options.map(o => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function TabBuscadorBaterias() {
  const [categoria, setCategoria] = useState('VEHICULOS');
  const [marca,     setMarca]     = useState('');
  const [modelo,    setModelo]    = useState('');
  const [anio,      setAnio]      = useState('');
  const [busqLibre, setBusqLibre] = useState('');

  // Datos filtrados por categoría
  const datosCat = useMemo(() =>
    YUASA_CATALOGO.filter(r => r.categoria === categoria && r.std),
  [categoria]);

  // Marcas disponibles
  const marcas = useMemo(() =>
    [...new Set(datosCat.map(r => r.marca))].filter(Boolean).sort(),
  [datosCat]);

  // Modelos según marca
  const modelos = useMemo(() => {
    if (!marca) return [];
    return [...new Set(
      datosCat.filter(r => r.marca === marca).map(r => r.modelo)
    )].filter(Boolean).sort();
  }, [datosCat, marca]);

  // Años según marca+modelo
  const anos = useMemo(() => {
    if (!marca || !modelo) return [];
    return [...new Set(
      datosCat
        .filter(r => r.marca === marca && r.modelo === modelo)
        .map(r => r.ano)
    )].filter(Boolean).sort();
  }, [datosCat, marca, modelo]);

  // Resultados finales
  const resultados = useMemo(() => {
    // Búsqueda libre
    if (busqLibre.trim().length >= 2) {
      const q = busqLibre.trim().toLowerCase();
      return datosCat.filter(r =>
        r.marca.toLowerCase().includes(q) ||
        r.modelo.toLowerCase().includes(q) ||
        r.motor?.toLowerCase().includes(q) ||
        r.std?.toLowerCase().includes(q) ||
        r.full?.toLowerCase().includes(q)
      ).slice(0, 20);
    }

    if (!marca) return [];
    let res = datosCat.filter(r => r.marca === marca);
    if (modelo) res = res.filter(r => r.modelo === modelo);
    if (anio)   res = res.filter(r => r.ano === anio);
    return res;
  }, [datosCat, marca, modelo, anio, busqLibre]);

  // Reset en cascada
  function onCategoria(v) {
    setCategoria(v); setMarca(''); setModelo(''); setAnio('');
  }
  function onMarca(v) { setMarca(v); setModelo(''); setAnio(''); }
  function onModelo(v) { setModelo(v); setAnio(''); }

  const modoLibre = busqLibre.trim().length >= 2;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* ── Tabs de categoría ─────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {CATEGORIAS.map(c => (
          <button key={c.key} onClick={() => onCategoria(c.key)} style={{
            background: categoria === c.key ? `${c.color}18` : '#1e1e2e',
            border: `1px solid ${categoria === c.key ? c.color : '#2a2a3e'}`,
            borderRadius: 8, color: categoria === c.key ? c.color : '#555',
            padding: '8px 18px', fontSize: 13, fontWeight: 700,
            cursor: 'pointer',
          }}>{c.label}</button>
        ))}
      </div>

      {/* ── Búsqueda libre ────────────────────────────────────────────────── */}
      <div style={{ position: 'relative' }}>
        <input
          value={busqLibre}
          onChange={e => setBusqLibre(e.target.value)}
          placeholder="Búsqueda libre: marca, modelo, motor, código de batería..."
          style={{
            background: '#13131f', border: `1px solid ${modoLibre ? '#4361ee' : '#2a2a3e'}`,
            borderRadius: 10, color: '#fff', padding: '12px 16px',
            fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box',
          }}
        />
        {busqLibre && (
          <button onClick={() => setBusqLibre('')} style={{
            position: 'absolute', right: 12, top: '50%',
            transform: 'translateY(-50%)',
            background: 'none', border: 'none', color: '#555',
            cursor: 'pointer', fontSize: 16,
          }}>✕</button>
        )}
      </div>

      {/* ── Selectores en cascada ─────────────────────────────────────────── */}
      {!modoLibre && (
        <div style={{ display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <Selector
            label="Marca"
            value={marca}
            onChange={onMarca}
            options={marcas}
            placeholder="— Selecciona marca —"
          />
          <Selector
            label="Modelo"
            value={modelo}
            onChange={onModelo}
            options={modelos}
            placeholder={marca ? '— Selecciona modelo —' : '— Primero elige marca —'}
            disabled={!marca}
          />
          <Selector
            label="Año / Versión"
            value={anio}
            onChange={setAnio}
            options={anos}
            placeholder={modelo ? '— Todos los años —' : '— Primero elige modelo —'}
            disabled={!modelo}
          />
        </div>
      )}

      {/* ── Contador de resultados ────────────────────────────────────────── */}
      {(marca || modoLibre) && (
        <div style={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'center' }}>
          <div style={{ color: '#555', fontSize: 12 }}>
            {resultados.length === 0
              ? 'Sin resultados'
              : `${resultados.length} resultado${resultados.length !== 1 ? 's' : ''}`}
            {modoLibre && ` para "${busqLibre}"`}
          </div>
          {(marca || modoLibre) && (
            <button onClick={() => {
              setMarca(''); setModelo(''); setAnio(''); setBusqLibre('');
            }} style={{
              background: 'none', border: '1px solid #2a2a3e',
              borderRadius: 6, color: '#555', padding: '4px 12px',
              fontSize: 11, cursor: 'pointer',
            }}>Limpiar</button>
          )}
        </div>
      )}

      {/* ── Resultados ───────────────────────────────────────────────────── */}
      {resultados.length === 0 && !marca && !modoLibre && (
        <div style={{ textAlign: 'center', padding: '48px 0',
          color: '#333', fontSize: 48 }}>
          🔋
          <div style={{ color: '#555', fontSize: 13, marginTop: 12 }}>
            Selecciona una marca o escribe en la búsqueda libre
          </div>
          <div style={{ color: '#333', fontSize: 11, marginTop: 6 }}>
            {datosCat.length} vehículos en la categoría {categoria}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {resultados.map((r, i) => (
          <ResultCard key={i} r={r} idx={i} />
        ))}
      </div>

      {/* Nota de fuente */}
      {resultados.length > 0 && (
        <div style={{ color: '#333', fontSize: 10, textAlign: 'right',
          paddingTop: 8, borderTop: '1px solid #1a1a2e' }}>
          Fuente: Catálogo YUASA 2026 · {YUASA_CATALOGO.length} referencias
        </div>
      )}
    </div>
  );
}
